/**
 * Institution Hero Section Component
 * Custom hero section for institutional landing pages
 */

import React from 'react';
import { Button, Grid, Column } from '@carbon/react';
import { ArrowRight, PlayFilled } from '@carbon/icons-react';
import type { CTAButton } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionHeroSectionProps {
  headline: string;
  subheadline?: string;
  description: string;
  ctaButtons: CTAButton[];
  backgroundImage?: string;
  videoUrl?: string;
  institutionName: string;
  institutionType: string;
}

const InstitutionHeroSection: React.FC<InstitutionHeroSectionProps> = ({
  headline,
  subheadline,
  description,
  ctaButtons,
  backgroundImage,
  videoUrl,
  institutionName,
  institutionType,
}) => {
  const handleCTAClick = (button: CTAButton) => {
    switch (button.action) {
      case 'demo':
        window.location.href = '/marketing/demo-request';
        break;
      case 'trial':
        window.location.href = '/marketing/trial-signup';
        break;
      case 'contact':
        window.location.href = '#contact';
        break;
      case 'calculator':
        window.location.href = '/marketing/calculators';
        break;
      case 'custom':
        if (button.url) {
          window.location.href = button.url;
        }
        break;
    }
  };

  const getInstitutionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      elementary: 'Elementary School',
      middle_school: 'Middle School',
      high_school: 'High School',
      district: 'School District',
      private_school: 'Private School',
      university: 'University',
    };
    return labels[type] || type;
  };

  return (
    <section
      className="institution-hero-section"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
      }}
    >
      <Grid narrow>
        <Column lg={10} md={8} sm={4}>
          <div className="hero-content">
            <div className="institution-badge">{getInstitutionTypeLabel(institutionType)}</div>

            <h1 className="hero-headline">{headline}</h1>

            {subheadline && <h2 className="hero-subheadline">{subheadline}</h2>}

            <p className="hero-description">{description}</p>

            <div className="hero-cta-buttons">
              {ctaButtons.map((button, index) => (
                <Button
                  key={index}
                  kind={button.style}
                  size="lg"
                  renderIcon={button.action === 'demo' ? PlayFilled : ArrowRight}
                  onClick={() => handleCTAClick(button)}
                >
                  {button.text}
                </Button>
              ))}
            </div>

            {videoUrl && (
              <div className="hero-video-link">
                <Button kind="ghost" size="sm" renderIcon={PlayFilled} onClick={() => window.open(videoUrl, '_blank')}>
                  Watch Overview Video
                </Button>
              </div>
            )}
          </div>
        </Column>

        {videoUrl && (
          <Column lg={6} md={8} sm={4} className="hero-media">
            <div className="video-placeholder">
              <PlayFilled size={48} />
              <p>Click to play video</p>
            </div>
          </Column>
        )}
      </Grid>

      <style>{`
        .institution-hero-section {
          padding: var(--cds-spacing-13) 0 var(--cds-spacing-11);
          background-size: cover;
          background-position: center;
          background-color: var(--cds-layer-01);
          position: relative;
          overflow: hidden;
        }

        .institution-hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(138, 63, 252, 0.95) 0%,
            rgba(138, 63, 252, 0.7) 50%,
            rgba(255, 199, 0, 0.1) 100%
          );
          z-index: 1;
        }

        .institution-hero-section > * {
          position: relative;
          z-index: 2;
        }

        .institution-badge {
          display: inline-block;
          padding: var(--cds-spacing-02) var(--cds-spacing-04);
          background-color: var(--cds-interactive-02, #ffc700);
          color: var(--cds-text-primary);
          font-size: var(--cds-label-01-font-size);
          font-weight: var(--cds-label-01-font-weight);
          text-transform: uppercase;
          letter-spacing: var(--cds-label-01-letter-spacing);
          margin-bottom: var(--cds-spacing-05);
          border-radius: var(--cds-border-radius);
        }

        .hero-headline {
          font-size: var(--cds-display-02-font-size);
          font-weight: var(--cds-display-02-font-weight);
          line-height: var(--cds-display-02-line-height);
          margin-bottom: var(--cds-spacing-05);
          color: var(--cds-text-inverse);
        }

        .hero-subheadline {
          font-size: var(--cds-heading-04-font-size);
          font-weight: var(--cds-heading-04-font-weight);
          line-height: var(--cds-heading-04-line-height);
          margin-bottom: var(--cds-spacing-06);
          color: var(--cds-text-inverse);
          opacity: 0.9;
        }

        .hero-description {
          font-size: var(--cds-body-long-02-font-size);
          line-height: var(--cds-body-long-02-line-height);
          margin-bottom: var(--cds-spacing-08);
          color: var(--cds-text-inverse);
          opacity: 0.85;
          max-width: 37.5rem;
        }

        .hero-cta-buttons {
          display: flex;
          gap: var(--cds-spacing-05);
          margin-bottom: var(--cds-spacing-06);
          flex-wrap: wrap;
        }

        .hero-video-link {
          margin-top: var(--cds-spacing-05);
        }

        .hero-media {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .video-placeholder {
          width: 100%;
          aspect-ratio: 16/9;
          background-color: var(--cds-overlay);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color var(--cds-duration-fast-02) var(--cds-ease-out);
          color: var(--cds-text-inverse);
          border-radius: var(--cds-border-radius);
        }

        .video-placeholder:hover {
          background-color: var(--cds-hover-ui);
          opacity: 0.7;
        }

        @media (max-width: 672px) {
          .hero-headline {
            font-size: 2rem;
          }

          .hero-subheadline {
            font-size: 1.25rem;
          }

          .hero-description {
            font-size: 1rem;
          }

          .hero-cta-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </section>
  );
};

export default InstitutionHeroSection;
